import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Search, ShieldAlert, Radar, Cpu, Loader2, Ban, UserX, WifiOff, Sparkles } from "lucide-react";
import { Header } from "@/pages/Operations";
import api from "@/lib/api";

const sevColor = {
  Critical: "text-red-400 bg-red-500/10 border-red-500/30",
  High: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  Medium: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
};

export default function Security() {
  const [data, setData] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [hunting, setHunting] = useState(false);

  useEffect(() => { api.get("/security/overview").then((r) => setData(r.data)).catch(() => {}); }, []);

  const hunt = async () => {
    if (!question.trim()) return;
    setHunting(true); setAnswer("");
    try {
      const r = await api.post("/security/hunt", { question });
      setAnswer(r.data.answer);
    } catch (e) { toast.error("Hunt query failed"); }
    finally { setHunting(false); }
  };

  const respond = async (action, target) => {
    try {
      await api.post("/security/respond", { action, target });
      toast.success(`${action} executed on ${target}`);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Admin privileges required" : "Action failed");
    }
  };

  const stats = data ? [
    { label: "Posture Score", value: data.posture_score, icon: ShieldAlert, color: "text-emerald-400" },
    { label: "Active Threats", value: data.active_threats, icon: ShieldAlert, color: "text-red-400" },
    { label: "Detections Today", value: data.detections_today, icon: Radar, color: "text-amber-400" },
    { label: "Hosts Monitored", value: data.hosts_monitored?.toLocaleString(), icon: Cpu, color: "text-cyan-400" },
  ] : [];

  return (
    <div data-testid="security-page" className="p-5 sm:p-8 max-w-[1300px] mx-auto">
      <Header icon={Lock} eyebrow="Security Center · SOC2" title="Threat Command"
        subtitle="AI-assisted threat hunting, security-enriched metadata recall, EDR correlation and 360 response." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((s, i) => (
          <div key={s.label} data-testid={`security-stat-${i}`} className="glass rounded-2xl p-5">
            <s.icon size={19} className={`${s.color} mb-3`} />
            <div className="text-2xl font-display font-extrabold">{s.value ?? "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* AI-Assisted Threat Hunting */}
      <div className="glass rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={15} className="text-purple-400" />
          <span className="font-display font-semibold text-sm">AI-Assisted Search</span>
          <span className="text-[10px] font-mono text-slate-500 ml-2">Ask in plain language — no query syntax</span>
        </div>
        <div className="flex items-center gap-2 bg-[#0A0C14]/60 border border-white/10 rounded-xl px-3 py-2">
          <Search size={15} className="text-slate-500" />
          <input data-testid="hunt-input" value={question} onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && hunt()}
            placeholder="e.g. Which hosts are beaconing to known C2 infrastructure in the last 24h?"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-600" />
          <button data-testid="hunt-button" onClick={hunt} disabled={hunting}
            className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white disabled:opacity-60">
            {hunting ? <Loader2 size={14} className="animate-spin" /> : "Hunt"}
          </button>
        </div>
        {answer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="hunt-answer"
            className="mt-3 p-4 rounded-xl border border-white/10 bg-white/5 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
            {answer}
          </motion.div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Detections + 360 Response */}
        <div className="glass rounded-2xl p-5">
          <div className="font-display font-semibold text-sm mb-3">Prioritized Detections · 360 Response</div>
          <div className="space-y-2">
            {(data?.detections || []).map((d, i) => (
              <div key={d.id} data-testid={`detection-${i}`} className="p-3 rounded-xl border border-white/10 bg-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[9px] font-mono uppercase px-2 py-0.5 rounded border ${sevColor[d.severity]}`}>{d.severity}</span>
                  <span className="text-sm font-medium">{d.type}</span>
                  <span className="ml-auto text-[10px] font-mono text-slate-500">{d.host} · score {d.score}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button data-testid={`isolate-${i}`} onClick={() => respond("Isolate Host", d.host)} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-white/10 text-slate-300 hover:border-red-500/40 hover:text-red-300 transition-colors"><WifiOff size={11} /> Isolate</button>
                  <button data-testid={`disable-${i}`} onClick={() => respond("Disable Account", d.host)} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-white/10 text-slate-300 hover:border-amber-500/40 hover:text-amber-300 transition-colors"><UserX size={11} /> Disable Account</button>
                  <button data-testid={`block-${i}`} onClick={() => respond("Block C2 Traffic", d.host)} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-white/10 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300 transition-colors"><Ban size={11} /> Block C2</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Vectra Recall */}
        <div className="glass rounded-2xl p-5">
          <div className="font-display font-semibold text-sm mb-1">Vectra Recall · Enriched Metadata</div>
          <div className="text-[10px] font-mono text-slate-500 mb-3">Searchable by host name, with EDR process correlation</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-[9px] uppercase text-slate-500 border-b border-white/10">
                  <th className="text-left py-2">Host</th><th className="text-left py-2">Process</th>
                  <th className="text-left py-2">Destination</th><th className="text-left py-2">Bytes</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recall_metadata || []).map((m, i) => (
                  <tr key={i} data-testid={`recall-${i}`} className="border-b border-white/5">
                    <td className="py-2 text-cyan-300">{m.host}</td>
                    <td className="py-2 text-purple-300">{m.process}</td>
                    <td className="py-2 text-slate-400">{m.dest}</td>
                    <td className="py-2 text-amber-300">{m.bytes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
